import axios, { AxiosResponse, AxiosError } from 'axios';
import { CommentData, CommentResponse, CommentListResponse, CommentActionResponse } from '../types/comment';

// Re-export comment types for convenience
export type { CommentData, CommentResponse, CommentListResponse, CommentActionResponse };

// Types for API responses
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    message: string;
    code?: string;
    details?: any;
  };
  meta?: {
    pagination?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
    timestamp?: string;
  };
}

export interface ApiError {
  message: string;
  code?: string;
  status?: number;
  details?: any;
}

// Cache for API responses
class ApiCache {
  private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();

  set(key: string, data: any, ttl: number = 300000) { // 5 minutes default
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  get(key: string): any | null {
    const cached = this.cache.get(key);
    if (!cached) return null;

    if (Date.now() - cached.timestamp > cached.ttl) {
      this.cache.delete(key);
      return null;
    }

    return cached.data;
  }

  clear(pattern?: string) {
    if (pattern) {
      const regex = new RegExp(pattern);
      const keysToDelete: string[] = [];
      this.cache.forEach((_, key) => {
        if (regex.test(key)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => this.cache.delete(key));
    } else {
      this.cache.clear();
    }
  }

  invalidate(key: string) {
    this.cache.delete(key);
  }
}

const apiCache = new ApiCache();

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and handle caching
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add request timestamp for debugging
    (config as any).metadata = { startTime: Date.now() };

    return config;
  },
  (error) => {
    return Promise.reject(formatError(error));
  }
);

// Response interceptor to handle errors and caching
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Add response time for debugging
    const endTime = Date.now();
    const startTime = (response.config as any).metadata?.startTime || endTime;
    (response.config as any).metadata = { 
      ...(response.config as any).metadata, 
      responseTime: endTime - startTime 
    };

    // Cache GET requests if successful
    if (response.config.method === 'get' && response.status === 200) {
      const cacheKey = `${response.config.url}?${JSON.stringify(response.config.params || {})}`;
      apiCache.set(cacheKey, response.data, 300000); // 5 minutes
    }

    return response;
  },
  (error: AxiosError) => {
    // Handle different error types
    if (error.response?.status === 401) {
      // Token expired or invalid - clear local state
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      apiCache.clear();
      
      // Only redirect if not already on login page
      // Use a small delay to allow current promise chains to settle
      if (!window.location.pathname.includes('/login')) {
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
      }
    }

    return Promise.reject(formatError(error));
  }
);

// Error formatting function
function formatError(error: any): ApiError {
  if (error.response) {
    // Server responded with error status
    return {
      message: error.response.data?.error?.message || error.response.data?.message || 'Server error occurred',
      code: error.response.data?.error?.code || error.response.status.toString(),
      status: error.response.status,
      details: error.response.data?.error?.details || error.response.data
    };
  } else if (error.request) {
    // Network error
    return {
      message: 'Network error. Please check your connection.',
      code: 'NETWORK_ERROR',
      status: 0
    };
  } else {
    // Other error
    return {
      message: error.message || 'An unexpected error occurred',
      code: 'UNKNOWN_ERROR'
    };
  }
}

// Upload service methods
export const uploadService = {
  // Upload images for posts
  uploadImages: async (files: FileList | File[]) => {
    if (files.length === 1) {
      // Single file upload
      const formData = new FormData();
      formData.append('file', files[0]);
      formData.append('folder', 'posts');
      formData.append('type', 'image');

      const response = await api.post('/upload/single', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } else {
      // Multiple files upload
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('files', file);
      });
      formData.append('folder', 'posts');

      const response = await api.post('/upload/multiple', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    }
  },

  // Upload profile picture
  uploadProfile: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'profiles');
    formData.append('type', 'image');

    const response = await api.post('/upload/single', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Upload video for posts
  uploadVideo: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'posts');
    formData.append('type', 'video');

    const response = await api.post('/upload/single', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Upload documents for verification
  uploadDocuments: async (files: FileList | File[]) => {
    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('files', file);
    });
    formData.append('folder', 'documents');

    const response = await api.post('/upload/multiple', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Crop image
  cropImage: async (file: File, cropData: { x: number; y: number; width: number; height: number }) => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('x', cropData.x.toString());
    formData.append('y', cropData.y.toString());
    formData.append('width', cropData.width.toString());
    formData.append('height', cropData.height.toString());

    const response = await api.post('/upload/crop', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Delete file
  deleteFile: async (type: string, filename: string) => {
    const response = await api.delete(`/upload/file/${type}/${filename}`);
    return response.data;
  },

  // Get file URL
  getFileUrl: (type: string, filename: string) => {
    return `${api.defaults.baseURL}/upload/file/${type}/${filename}`;
  },
};

// API Service methods
export const apiService = {
  // Generic methods
  async get<T>(url: string, params?: any, useCache: boolean = true): Promise<ApiResponse<T>> {
    try {
      // Check cache first for GET requests
      if (useCache) {
        const cacheKey = `${url}?${JSON.stringify(params || {})}`;
        const cached = apiCache.get(cacheKey);
        if (cached) {
          return cached;
        }
      }

      const response = await api.get(url, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async post<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await api.post(url, data);
      // Invalidate related cache entries
      apiCache.clear(url.split('/')[1]); // Clear cache for the resource type
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await api.put(url, data);
      // Invalidate related cache entries
      apiCache.clear(url.split('/')[1]);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async delete<T>(url: string): Promise<ApiResponse<T>> {
    try {
      const response = await api.delete(url);
      // Invalidate related cache entries
      apiCache.clear(url.split('/')[1]);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Clear cache
  clearCache: (pattern?: string) => apiCache.clear(pattern),
  invalidateCache: (key: string) => apiCache.invalidate(key),
};

// OTP service (commented out - not using OTP for now)
/*
export const otpService = {
  async sendOTP(email: string, purpose: 'signup' | 'login' | 'password-reset' = 'signup') {
    return apiService.post('/otp/send', { email, purpose });
  },

  async verifyOTP(email: string, otp: string, purpose: 'signup' | 'login' | 'password-reset' = 'signup') {
    return apiService.post('/otp/verify', { email, otp, purpose });
  },

  async resendOTP(email: string, purpose: 'signup' | 'login' | 'password-reset' = 'signup') {
    return apiService.post('/otp/resend', { email, purpose });
  },

  async checkOTPStatus(email: string, purpose: 'signup' | 'login' | 'password-reset' = 'signup') {
    return apiService.get(`/otp/status/${email}?purpose=${purpose}`);
  }
};
*/

// Authentication service
export const authService = {
  async login(credentials: { email: string; password: string }) {
    return apiService.post('/auth/login', credentials);
  },

  async signup(userData: { firstName: string; lastName: string; email: string; password: string; role: string; sports: string[] }) {
    return apiService.post('/auth/signup', userData);
  },

  async logout() {
    const response = await apiService.post('/auth/logout');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    apiCache.clear();
    return response;
  },

  async refreshToken() {
    return apiService.post('/auth/refresh');
  },

  async forgotPassword(email: string) {
    return apiService.post('/auth/forgot-password', { email });
  },

  async resetPassword(token: string, password: string) {
    return apiService.post('/auth/reset-password', { token, password });
  },

  async verifyEmail(token: string) {
    return apiService.post('/auth/verify-email', { token });
  },
};

// User service
export const userService = {
  async getProfile(userId?: string) {
    const url = userId ? `/users/${userId}` : '/users/profile';
    return apiService.get(url);
  },

  async updateProfile(data: any) {
    return apiService.put('/users/profile', data);
  },

  async getUsers(params?: { page?: number; limit?: number; search?: string; role?: string }) {
    return apiService.get<{ items: any[]; pagination: any }>('/users', params);
  },

  async followUser(userId: string, message?: string) {
    return apiService.post(`/users/${userId}/follow`, { message });
  },

  async unfollowUser(userId: string) {
    return apiService.post(`/users/${userId}/follow`); // Same endpoint, but will unfollow if already following
  },

  async acceptFollowRequest(userId: string) {
    return apiService.post(`/users/${userId}/follow/accept`);
  },

  async rejectFollowRequest(userId: string) {
    return apiService.post(`/users/${userId}/follow/reject`);
  },

  async getFollowRequests(params?: { page?: number; limit?: number }) {
    return apiService.get<{ items: any[]; pagination: any }>('/users/follow-requests', params);
  },

  async getFollowStatus(userId: string) {
    return apiService.get(`/users/${userId}/follow-status`);
  },


  async getFollowers(userId: string, params?: { page?: number; limit?: number }) {
    return apiService.get<{ items: any[]; pagination: any }>(`/users/${userId}/followers`, params);
  },

  async getFollowing(userId: string, params?: { page?: number; limit?: number }) {
    return apiService.get<{ items: any[]; pagination: any }>(`/users/${userId}/following`, params);
  },

  async searchUsers(query: string, params?: { page?: number; limit?: number; role?: string }) {
    return apiService.get('/users/search', { q: query, ...params });
  },
};

// Post service
export const postService = {
  async getPosts(params?: { page?: number; limit?: number; userId?: string; communityId?: string }) {
    return apiService.get<{ items: any[]; pagination: any }>('/posts', params);
  },

  async getPost(postId: string) {
    return apiService.get(`/posts/${postId}`);
  },

  async createPost(data: { content: string; images?: string[]; communityId?: string }) {
    return apiService.post('/posts', data);
  },

  async updatePost(postId: string, data: { content: string; images?: string[] }) {
    return apiService.put(`/posts/${postId}`, data);
  },

  async deletePost(postId: string) {
    return apiService.delete(`/posts/${postId}`);
  },

  async likePost(postId: string) {
    return apiService.post(`/posts/${postId}/like`);
  },

  async unlikePost(postId: string) {
    return apiService.delete(`/posts/${postId}/like`);
  },

  async sharePost(postId: string) {
    return apiService.post(`/posts/${postId}/share`);
  },
};

// Comment service
export const commentService = {
  async getComments(postId: string, params?: { page?: number; limit?: number }): Promise<CommentListResponse> {
    const response = await apiService.get(`/comments/post/${postId}`, params);
    return response as CommentListResponse;
  },

  async createComment(data: { content: string; post: string; parentComment?: string }): Promise<CommentResponse> {
    const response = await apiService.post('/comments', data);
    return response as CommentResponse;
  },

  async updateComment(commentId: string, data: { content: string }): Promise<CommentResponse> {
    const response = await apiService.put(`/comments/${commentId}`, data);
    return response as CommentResponse;
  },

  async deleteComment(commentId: string): Promise<{ success: boolean; data: { message: string } }> {
    const response = await apiService.delete(`/comments/${commentId}`);
    return response as { success: boolean; data: { message: string } };
  },

  async likeComment(commentId: string): Promise<CommentActionResponse> {
    const response = await apiService.post(`/comments/${commentId}/like`);
    return response as CommentActionResponse;
  },

  async dislikeComment(commentId: string): Promise<CommentActionResponse> {
    const response = await apiService.post(`/comments/${commentId}/dislike`);
    return response as CommentActionResponse;
  },
};

// Story service
export const storyService = {
  async getFeedStories() {
    return apiService.get('/stories/feed');
  },

  async getUserStories(userId: string) {
    return apiService.get(`/stories/user/${userId}`);
  },

  async createStory(data: {
    type: 'image' | 'video' | 'text';
    url?: string;
    text?: string;
    backgroundColor?: string;
    textColor?: string;
    font?: string;
    duration?: number;
    visibility?: string;
    allowReplies?: boolean;
  }) {
    return apiService.post('/stories', data);
  },

  async viewStory(storyId: string) {
    return apiService.post(`/stories/${storyId}/view`);
  },

  async reactToStory(storyId: string, emoji: string) {
    return apiService.post(`/stories/${storyId}/react`, { emoji });
  },

  async removeReaction(storyId: string) {
    return apiService.delete(`/stories/${storyId}/react`);
  },

  async deleteStory(storyId: string) {
    return apiService.delete(`/stories/${storyId}`);
  },

  async replyToStory(storyId: string, message: string) {
    return apiService.post(`/stories/${storyId}/reply`, { message });
  },
};

// Community service
export const communityService = {
  async getCommunities(params?: { page?: number; limit?: number; search?: string; sport?: string }) {
    return apiService.get<{ items: any[]; pagination: any }>('/communities', params);
  },

  async getCommunity(communityId: string) {
    return apiService.get(`/communities/${communityId}`);
  },

  async createCommunity(data: { name: string; description: string; sport: string; isPrivate: boolean }) {
    return apiService.post('/communities', data);
  },

  async updateCommunity(communityId: string, data: any) {
    return apiService.put(`/communities/${communityId}`, data);
  },

  async deleteCommunity(communityId: string) {
    return apiService.delete(`/communities/${communityId}`);
  },

  async joinCommunity(communityId: string) {
    return apiService.post(`/communities/${communityId}/join`);
  },

  async leaveCommunity(communityId: string) {
    return apiService.delete(`/communities/${communityId}/join`);
  },

  async getMembers(communityId: string, params?: { page?: number; limit?: number }) {
    return apiService.get<{ items: any[]; pagination: any }>(`/communities/${communityId}/members`, params);
  },

  async getCommunityPosts(communityId: string, params?: { page?: number; limit?: number }) {
    return apiService.get<{ items: any[]; pagination: any }>(`/communities/${communityId}/posts`, params);
  },

  async searchCommunities(query: string, params?: { page?: number; limit?: number; sport?: string }) {
    return apiService.get('/communities/search', { q: query, ...params });
  },
};

// Tournament service
export const tournamentService = {
  async getTournaments(params?: { page?: number; limit?: number; search?: string; sport?: string; status?: string }) {
    return apiService.get<{ items: any[]; pagination: any }>('/tournaments', params);
  },

  async getTournament(tournamentId: string) {
    return apiService.get(`/tournaments/${tournamentId}`);
  },

  async createTournament(data: any) {
    return apiService.post('/tournaments', data);
  },

  async updateTournament(tournamentId: string, data: any) {
    return apiService.put(`/tournaments/${tournamentId}`, data);
  },

  async deleteTournament(tournamentId: string) {
    return apiService.delete(`/tournaments/${tournamentId}`);
  },

  async joinTournament(tournamentId: string) {
    return apiService.post(`/tournaments/${tournamentId}/join`);
  },

  async leaveTournament(tournamentId: string) {
    return apiService.delete(`/tournaments/${tournamentId}/join`);
  },

  async getParticipants(tournamentId: string, params?: { page?: number; limit?: number }) {
    return apiService.get<{ items: any[]; pagination: any }>(`/tournaments/${tournamentId}/participants`, params);
  },

  async searchTournaments(query: string, params?: { page?: number; limit?: number; sport?: string }) {
    return apiService.get('/tournaments/search', { q: query, ...params });
  },
};

// Achievement service
export const achievementService = {
  async getAchievements(params?: { page?: number; limit?: number; userId?: string; status?: string }) {
    return apiService.get<{ items: any[]; pagination: any }>('/achievements', params);
  },

  async getAchievement(achievementId: string) {
    return apiService.get(`/achievements/${achievementId}`);
  },

  async submitAchievement(data: { title: string; description: string; sport: string; documents: string[]; year?: number }) {
    return apiService.post('/achievements', data);
  },

  async updateAchievement(achievementId: string, data: any) {
    return apiService.put(`/achievements/${achievementId}`, data);
  },

  async deleteAchievement(achievementId: string) {
    return apiService.delete(`/achievements/${achievementId}`);
  },

  async getUserAchievements(userId: string, params?: { page?: number; limit?: number }) {
    return apiService.get<{ items: any[]; pagination: any }>(`/users/${userId}/achievements`, params);
  },
};

// Chat service
export const chatService = {
  async getChats(params?: { page?: number; limit?: number }) {
    return apiService.get<{ items: any[]; pagination: any }>('/chats', params);
  },

  async getChat(chatId: string) {
    return apiService.get(`/chats/${chatId}`);
  },

  async createChat(participantId: string) {
    return apiService.post('/chats', { participantId });
  },

  async getMessages(chatId: string, params?: { page?: number; limit?: number }) {
    return apiService.get<{ items: any[]; pagination: any }>(`/chats/${chatId}/messages`, params);
  },

  async sendMessage(chatId: string, data: { content: string; type?: string; attachments?: string[] }) {
    return apiService.post(`/chats/${chatId}/messages`, data);
  },

  async markAsRead(chatId: string) {
    return apiService.put(`/chats/${chatId}/read`);
  },

  async deleteMessage(chatId: string, messageId: string) {
    return apiService.delete(`/chats/${chatId}/messages/${messageId}`);
  },
};

// Admin service
export const adminService = {
  async getDashboardStats() {
    return apiService.get('/admin/dashboard/stats');
  },

  async getUsers(params?: { page?: number; limit?: number; search?: string; status?: string }) {
    return apiService.get<{ items: any[]; pagination: any }>('/admin/users', params);
  },

  async approveUser(userId: string) {
    return apiService.put(`/admin/users/${userId}/approve`);
  },

  async rejectUser(userId: string, reason?: string) {
    return apiService.put(`/admin/users/${userId}/reject`, { reason });
  },

  async blockUser(userId: string, reason?: string) {
    return apiService.put(`/admin/users/${userId}/block`, { reason });
  },

  async unblockUser(userId: string) {
    return apiService.put(`/admin/users/${userId}/unblock`);
  },

  async getCommunities(params?: { page?: number; limit?: number; search?: string; status?: string }) {
    return apiService.get<{ items: any[]; pagination: any }>('/admin/communities', params);
  },

  async moderateCommunity(communityId: string, action: string, reason?: string) {
    return apiService.put(`/admin/communities/${communityId}/moderate`, { action, reason });
  },

  async getTournaments(params?: { page?: number; limit?: number; search?: string; status?: string }) {
    return apiService.get<{ items: any[]; pagination: any }>('/tournaments', params);
  },

  async getAchievements(params?: { page?: number; limit?: number; search?: string; status?: string }) {
    return apiService.get<{ items: any[]; pagination: any }>('/admin/achievements', params);
  },

  async approveAchievement(achievementId: string) {
    return apiService.put(`/admin/achievements/${achievementId}/approve`);
  },

  async rejectAchievement(achievementId: string, reason?: string) {
    return apiService.put(`/admin/achievements/${achievementId}/reject`, { reason });
  },

  async getSettings() {
    return apiService.get('/admin/settings');
  },

  async updateSettings(settings: any) {
    return apiService.put('/admin/settings', settings);
  },
};

export default api;